import React, { useEffect, useState } from 'react'
import axios from 'axios'
import _ from 'lodash'
import { toast } from 'react-toastify'

import {
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Chip,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material'

import {
  DeleteOutlined as DeleteOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  HighlightOff as HighlightOffIcon,
  InfoOutlined,
  SaveOutlined as SaveOutlinedIcon,
} from '@mui/icons-material/'

import { TOAST_SETTINGS_ERROR } from '../../../utility/constants'

import { useAuthContext } from '../../../hooks/useAuthContext'
import ConfirmationBox from '../../../components/Dashboard/ConfirmationBox'
import { useNavigate } from 'react-router-dom'
import { getAxiosOptions } from '../../../services/serviceHelper'

const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

const ListConfig = () => {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const [lists, setLists] = useState([])
  const [listData, setListData] = useState([])

  const [isListsConfigDialogOpen, setIsListsConfigDialogOpen] = useState(false)
  const [isListDataEditDialogOpen, setIsListDataEditDialogOpen] = useState(false)
  const [isAddMdkDialogOpen, setIsAddMdkDialogOpen] = useState(false)

  const [deletingListId, setDeletingListId] = useState(-1)
  const [deletingListDataId, setDeletingListDataId] = useState(-1)
  const [deletingMdkIndex, setDeletingMdkIndex] = useState(-1)
  const [editingListDataId, setEditingListDataId] = useState(-1)
  const [metadataKeys, setMetadataKeys] = useState([])
  const [mdkValue, setMdkValue] = useState('')

  const [isDeleteListConfirmationBoxOpen, setIsDeleteListConfirmationBoxOpen] = useState(false)
  const [isDeleteListDataConfirmationBoxOpen, setIsDeleteListDataConfirmationBoxOpen] =
    useState(false)
  const [isDeleteMdkConfirmationBoxOpen, setIsDeleteMdkConfirmationBoxOpen] = useState(false)

  async function deleteList(listId) {
    if (!user) return
    const oldLists = lists
    try {
      await axios.delete(
        `${NODE_BASE_URL}/admin/template/list/delete-list/${listId}`,
        getAxiosOptions(user)
      )

      const newLists = lists.filter((l) => l.id !== listId)
      setLists(newLists)
    } catch (_err) {
      toast.error(
        'Unable to delete list. It may be currently used in a task.',
        TOAST_SETTINGS_ERROR
      )

      setLists(oldLists)
    }
  }

  async function deleteListData(listDataId) {
    if (!user) return
    const oldListData = listData
    try {
      await axios.delete(
        `${NODE_BASE_URL}/admin/template/list/delete-list-data/${listDataId}`,
        getAxiosOptions(user)
      )

      const newListData = listData.filter((ld) => ld.id !== listDataId)
      setListData(newListData)
    } catch (_err) {
      toast.error('Unable to delete list item.', TOAST_SETTINGS_ERROR)

      setListData(oldListData)
    }
  }

  const addNewList = async () => {
    if (!user) return
    await axios.post(`${NODE_BASE_URL}/admin/template/list/add-list`, null, getAxiosOptions(user))
    await getListsFromServer()
  }

  async function getListsFromServer() {
    if (!user) return
    const lists = (await axios.get(`${NODE_BASE_URL}/template/list/company`, getAxiosOptions(user)))
      .data

    setLists(lists)
  }

  async function handleListDataDialogOpen(listId) {
    await getListDataFromServer(listId)

    setIsListsConfigDialogOpen(true)
    const mdk = lists.find((l) => l.id == listId)?.metadata_keys

    if (mdk) {
      setMetadataKeys(mdk.split(',').map((elem) => elem.trim()))
    } else {
      setMetadataKeys([])
    }
  }

  async function handleListNameChange(e) {
    if (!user) return
    const currListId = lists.find((l) => l.id === listData[0]?.list_id)?.id

    setLists((prev) => {
      let newLists = prev.filter((l) => l.id !== currListId)
      const newList = {
        ...lists.find((l) => l.id === listData[0]?.list_id),
        list_name: e.target.value,
      }
      newLists.push(newList)
      return newLists
    })

    await axios.put(
      `${NODE_BASE_URL}/admin/template/list/edit-list-name`,
      {
        list_id: currListId,
        list_name: e.target.value,
      },
      getAxiosOptions(user)
    )

    await getListsFromServer()
  }

  async function handleListDataNameChange(e) {
    if (!user) return
    setListData((prev) => {
      let newListData = prev.filter((ld) => ld.id !== editingListDataId)
      const newListDatum = {
        ...listData.find((ld) => ld.id === editingListDataId),
        value: e.target.value,
      }
      newListData.push(newListDatum)

      newListData = newListData.sort((a, b) => a.sequence - b.sequence)

      return newListData
    })

    await axios.put(
      `${NODE_BASE_URL}/admin/template/list/edit-list-data-name`,
      {
        list_data_id: editingListDataId,
        list_data_name: e.target.value,
      },
      getAxiosOptions(user)
    )
  }

  async function handleDeleteMdk(i) {
    if (!user) return
    let metadataKeysTempArr = [...metadataKeys]
    metadataKeysTempArr.splice(i, 1)
    setMetadataKeys(metadataKeysTempArr)

    let mdKeysStr = metadataKeysTempArr.join(',')

    if (metadataKeysTempArr.length === 0) {
      mdKeysStr = null
    }

    const list_id = lists.find((l) => l.id === listData[0]?.list_id).id
    await axios.put(
      `${NODE_BASE_URL}/admin/template/list/edit-list-metadata`,
      {
        list_id,
        metadata: mdKeysStr,
      },
      getAxiosOptions(user)
    )

    await getListsFromServer()
  }

  async function handleAddListMetadata() {
    let newMdk = mdkValue.toLowerCase()
    newMdk = newMdk.replace(/[^a-z]/gi, '')

    if (newMdk == '' || metadataKeys.includes(newMdk)) {
      //return if mdk is empty or already exists
      return
    }

    setMetadataKeys([...metadataKeys, newMdk])

    await axios.put(
      `${NODE_BASE_URL}/admin/template/list/edit-list-metadata`,
      {
        list_id: lists.find((l) => l.id === listData[0]?.list_id).id,
        metadata: [...metadataKeys, newMdk].join(','),
      },
      getAxiosOptions(user)
    )

    await getListsFromServer()
  }

  async function handleMetadataValueChange(e) {
    if (!user) return
    const list_data_id = editingListDataId
    const metadata_key = e.target.name
    const metadata_value = e.target.value

    await axios.put(
      `${NODE_BASE_URL}/admin/template/list/edit-list-data-metadata`,
      { list_data_id, metadata_key, metadata_value },
      getAxiosOptions(user)
    )

    await getListDataFromServer(listData[0]?.list_id)
  }

  async function getListsFromServer() {
    if (!user) return
    const lists = (await axios.get(`${NODE_BASE_URL}/template/list/company`, getAxiosOptions(user)))
      .data

    setLists(lists)
  }

  async function getListDataFromServer(listId) {
    if (!user) return
    const listData = (
      await axios.get(`${NODE_BASE_URL}/template/list-data/list/${listId}`, getAxiosOptions(user))
    ).data

    setListData(listData)
  }

  useEffect(() => {
    if (user) {
      getListsFromServer()
    }
  }, [user])

  const getHelperText = (listData, selectedListDataId) => {
    const otherItems = listData.filter((it) => it.id !== selectedListDataId)
    const item = listData.find((it) => it.id === selectedListDataId)
    const isDuplicate = otherItems.some((it) => it?.value == item?.value)
    return isDuplicate ? 'Duplicate list item!' : false
  }

  return (
    lists && (
      <>
        <Container>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0px 8px',
            }}
          >
            <Typography variant="h5" color="textSecondary">
              Manage List
            </Typography>
            <span>
              <Button
                color="primary"
                variant="contained"
                form="data-entry-form"
                style={{ height: 30, marginRight: 24 }}
                onClick={() => navigate('/admin/lddm')}
              >
                Manage P-C Relation
              </Button>
              <Button
                color="primary"
                variant="contained"
                form="data-entry-form"
                style={{ height: 30 }}
                onClick={addNewList}
              >
                Add New List
              </Button>
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              marginTop: 24,
            }}
          >
            {lists.map((l) =>
              lists.length > 1 ? (
                //with delete list icon
                <React.Fragment key={l.id}>
                  <Chip
                    label={l.list_name}
                    variant="outlined"
                    onClick={() => handleListDataDialogOpen(l.id)}
                    onDelete={() => {
                      setDeletingListId(l.id)
                      setIsDeleteListConfirmationBoxOpen(true)
                    }}
                    deleteIcon={<DeleteOutlinedIcon />}
                  />
                  {isDeleteListConfirmationBoxOpen && (
                    <ConfirmationBox
                      title={`Are you sure you want to delete list "${
                        lists.find((l) => l.id == deletingListId)?.list_name
                      }"?`}
                      isDialogOpen={isDeleteListConfirmationBoxOpen}
                      setIsDialogOpen={setIsDeleteListConfirmationBoxOpen}
                      onYes={() => {
                        deleteList(deletingListId)
                        setDeletingListId(-1)
                        setIsDeleteListConfirmationBoxOpen(false)
                      }}
                    />
                  )}
                </React.Fragment>
              ) : (
                //don't allow last list to be deleted
                <Chip
                  key={l.id}
                  label={l.list_name}
                  variant="outlined"
                  onClick={() => handleListDataDialogOpen(l.id)}
                />
              )
            )}
          </div>
        </Container>
        {/* List Config Dialog  */}
        <Dialog open={isListsConfigDialogOpen} onClose={() => setIsListsConfigDialogOpen(false)}>
          <DialogTitle>
            <Grid container justifyContent="space-between" alignItems="center">
              <TextField
                value={lists.find((l) => l.id === listData[0]?.list_id)?.list_name}
                size="small"
                label="List Name"
                variant="outlined"
                color="primary"
                onChange={(e) => handleListNameChange(e)}
              />
              <IconButton
                align="right"
                onClick={() => setIsListsConfigDialogOpen(false)}
                size="small"
              >
                <HighlightOffIcon />
              </IconButton>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <hr />
            <Typography variant="caption" color="textSecondary">
              Metadata Keys
            </Typography>
            {metadataKeys.map((mdk, i) => (
              <React.Fragment key={i}>
                <Typography variant="body1" color="textSecondary">
                  {mdk}
                  <IconButton
                    onClick={() => {
                      setDeletingMdkIndex(i)
                      setIsDeleteMdkConfirmationBoxOpen(true)
                    }}
                    size="small"
                  >
                    <DeleteOutlinedIcon />
                  </IconButton>
                </Typography>
                <ConfirmationBox
                  title={`Deleting this metadata key could lead to unintended downstream 
                      effects if associated metadata values exist. 
                      Delete the metadata key anyway?`}
                  isDialogOpen={isDeleteMdkConfirmationBoxOpen}
                  setIsDialogOpen={setIsDeleteMdkConfirmationBoxOpen}
                  onYes={() => {
                    handleDeleteMdk(deletingMdkIndex)
                    setIsDeleteMdkConfirmationBoxOpen(false)
                    setDeletingMdkIndex(-1)
                  }}
                />
              </React.Fragment>
            ))}
            <br />
            <br />
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setIsAddMdkDialogOpen(true)}
              size="small"
            >
              Add New Metadata Key
            </Button>
            <hr />
            <Typography variant="caption" color="textSecondary">
              List Items
            </Typography>
            {listData &&
              listData.map((ld) => (
                <div
                  style={{ margin: 20, display: 'flex', alignItems: 'center', gap: 8 }}
                  key={ld.id}
                >
                  {getHelperText(listData, ld.id) ? (
                    <Tooltip title={getHelperText(listData, ld.id)}>
                      <InfoOutlined style={{ fontSize: 16, color: 'red', cursor: 'pointer' }} />
                    </Tooltip>
                  ) : null}{' '}
                  <span style={{ whiteSpace: 'pre-line' }}>{ld.value}</span>
                  <IconButton
                    onClick={() => {
                      setIsListDataEditDialogOpen(true)
                      setEditingListDataId(ld.id)
                    }}
                    size="small"
                  >
                    <EditOutlinedIcon />
                  </IconButton>
                  {listData?.length > 1 && (
                    <IconButton
                      onClick={() => {
                        setDeletingListDataId(ld.id)
                        setIsDeleteListDataConfirmationBoxOpen(true)
                      }}
                      size="small"
                    >
                      <DeleteOutlinedIcon />
                    </IconButton>
                  )}
                  {isDeleteListDataConfirmationBoxOpen && (
                    <ConfirmationBox
                      title={`Are you sure you want to delete list item "${
                        listData.find((ld) => ld.id == deletingListDataId)?.value
                      }"?`}
                      isDialogOpen={isDeleteListDataConfirmationBoxOpen}
                      setIsDialogOpen={setIsDeleteListDataConfirmationBoxOpen}
                      onYes={() => {
                        deleteListData(deletingListDataId)
                        setDeletingListDataId(-1)
                        setIsDeleteListDataConfirmationBoxOpen(false)
                      }}
                    />
                  )}
                </div>
              ))}
            <Button
              color="primary"
              variant="outlined"
              type="button"
              size="small"
              onClick={async () => {
                if (!user) return
                const list_id = listData[0]?.list_id
                await axios.post(
                  `${NODE_BASE_URL}/admin/template/list/add-list-data`,
                  { list_id },
                  getAxiosOptions(user)
                )
                await getListDataFromServer(list_id)
              }}
            >
              Add List Item
            </Button>
          </DialogContent>
        </Dialog>
        {/* Edit List Data Dialog */}
        <Dialog
          open={isListDataEditDialogOpen}
          onClose={() => {
            setEditingListDataId(-1)
            setIsListDataEditDialogOpen(false)
          }}
        >
          <DialogTitle>
            <Grid container justifyContent="space-between" alignItems="center">
              <IconButton
                align="right"
                onClick={() => {
                  setEditingListDataId(-1)
                  setIsListDataEditDialogOpen(false)
                }}
                size="small"
              >
                <HighlightOffIcon />
              </IconButton>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <>
              <br />
              <TextField
                value={listData.find((ld) => ld.id === editingListDataId)?.value}
                label="List Item Name"
                size="small"
                variant="outlined"
                multiline={true}
                rows={5}
                color="primary"
                error={getHelperText(listData, editingListDataId)}
                helperText={getHelperText(listData, editingListDataId)}
                onChange={(e) => handleListDataNameChange(e)}
              />
              <br />
              <br />
              {metadataKeys && metadataKeys.length > 0 && (
                <Table stickyHeader aria-label="simple table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ padding: '10px' }}>Metadata Key</TableCell>
                      <TableCell sx={{ padding: '10px' }}>Metadata Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metadataKeys.map((mdk, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ padding: '10px' }}>{mdk}</TableCell>
                        <TableCell sx={{ padding: '10px' }}>
                          <TextField
                            name={mdk}
                            value={
                              listData.find((ld) => ld.id == editingListDataId)?.metadata
                                ? listData.find((ld) => ld.id == editingListDataId)?.metadata[mdk]
                                : ''
                            }
                            size="small"
                            variant="outlined"
                            color="primary"
                            onChange={(e) => {
                              handleMetadataValueChange(e)
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          </DialogContent>
        </Dialog>
        {/* Add Metadata Key Dialog */}
        <Dialog
          open={isAddMdkDialogOpen}
          onClose={() => {
            setMdkValue('')
            setIsAddMdkDialogOpen(false)
          }}
        >
          <DialogTitle>
            <Grid container justifyContent="space-between" alignItems="center">
              <IconButton
                onClick={() => {
                  setMdkValue('')
                  setIsAddMdkDialogOpen(false)
                }}
                size="small"
              >
                <HighlightOffIcon />
              </IconButton>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <TextField
              value={mdkValue}
              size="small"
              variant="outlined"
              color="primary"
              onChange={(e) => setMdkValue(e.target.value)}
            />
            <IconButton
              align="right"
              onClick={() => {
                handleAddListMetadata()
                setIsAddMdkDialogOpen(false)
                setMdkValue('')
              }}
              size="small"
            >
              <SaveOutlinedIcon />
            </IconButton>
          </DialogContent>
        </Dialog>
      </>
    )
  )
}

export default ListConfig
